import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TableSortLabel,
  SxProps,
  Theme,
} from "@mui/material";
import React, { ReactNode } from "react";

import { useCelleraTheme } from "../../theme/useCelleraTheme";

export interface CustomTableColumn<T> {
  /**
   * Unique identifier for the column
   */
  id: string;
  /**
   * Display label for the column header
   */
  label: string;
  /**
   * Function to extract the value from the row data
   */
  getValue: (row: T) => ReactNode;
  /**
   * Optional width for the column
   */
  width?: string | number;
  /**
   * Optional alignment for the column content
   */
  align?: "left" | "center" | "right";
  /**
   * Optional custom cell styling
   */
  cellSx?: object;
  /**
   * Whether the column is sortable (client-side)
   */
  sortable?: boolean;
  /**
   * Function to extract a sortable value from the row
   */
  sortAccessor?: (row: T) => string | number | Date | null | undefined;
}

export interface CustomTableProps<T> {
  /**
   * Array of data to display in the table
   */
  data?: T[];
  /**
   * Array of column definitions
   */
  columns?: CustomTableColumn<T>[];
  /**
   * Total number of items for pagination
   */
  totalCount?: number;
  /**
   * Current page (0-based)
   */
  page: number;
  /**
   * Number of rows per page
   */
  rowsPerPage?: number;
  /**
   * Available rows per page options
   */
  rowsPerPageOptions?: number[];
  /**
   * Callback when page changes
   */
  onPageChange: (event: unknown, newPage: number) => void;
  /**
   * Callback when rows per page changes
   */
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the table is currently loading
   */
  isLoading?: boolean;
  /**
   * Custom loading message
   */
  loadingMessage?: string;
  /**
   * Custom empty state content
   */
  emptyStateContent?: ReactNode;
  /**
   * Whether to show first/last page buttons
   */
  showFirstButton?: boolean;
  /**
   * Whether to show last page button
   */
  showLastButton?: boolean;
  /**
   * Optional custom table container styling
   */
  tableContainerSx?: object;
  /**
   * Optional custom table styling
   */
  tableSx?: object;
  /**
   * Optional custom paper styling
   */
  paperSx?: SxProps<Theme>;
  /**
   * Optional custom row styling function
   */
  getRowSx?: (row: T, index: number) => object;
  /**
   * Optional row key function (defaults to using index)
   */
  getRowKey?: (row: T, index: number) => string | number;
  /**
   * Whether to enable sticky header
   */
  stickyHeader?: boolean;
  /**
   * Minimum height for the table container
   */
  minHeight?: number;
  /**
   * Callback when sort changes (for server-side sorting)
   */
  onSortChange?: (columnId: string, direction: "asc" | "desc") => void;
  /**
   * Current sort column ID (for server-side sorting)
   */
  sortBy?: string | null;
  /**
   * Current sort direction (for server-side sorting)
   */
  sortDirection?: "asc" | "desc";
  /**
   * Whether to disable client-side sorting (use when implementing server-side sorting)
   */
  disableClientSideSort?: boolean;

  onClickRow?: (row: T) => void;
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function CustomTable<T>({
  data = [],
  columns = [],
  totalCount = 0,
  page,
  rowsPerPage = 10,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  onPageChange,
  onRowsPerPageChange,
  isLoading = false,
  loadingMessage = "Loading data...",
  emptyStateContent,
  showFirstButton = true,
  showLastButton = true,
  tableContainerSx,
  paperSx,
  tableSx,
  getRowSx,
  getRowKey,
  stickyHeader = true,
  minHeight = 400,
  onSortChange,
  sortBy: externalSortBy,
  sortDirection: externalSortDirection,
  disableClientSideSort = false,
  onClickRow,
}: CustomTableProps<T>) {
  const { colors } = useCelleraTheme();

  // client-side sorting state (only used if server-side sorting is not enabled)
  const [internalOrderBy, setInternalOrderBy] = React.useState<string | null>(
    null
  );
  const [internalOrder, setInternalOrder] = React.useState<"asc" | "desc">(
    "asc"
  );

  // Use external sort state if provided, otherwise use internal state
  const orderBy =
    externalSortBy !== undefined ? externalSortBy : internalOrderBy;
  const order =
    externalSortDirection !== undefined ? externalSortDirection : internalOrder;

  const handleRequestSort = (columnId: string) => {
    if (onSortChange) {
      // Server-side sorting
      if (orderBy === columnId) {
        onSortChange(columnId, order === "asc" ? "desc" : "asc");
      } else {
        onSortChange(columnId, "asc");
      }
    } else if (!disableClientSideSort) {
      // Client-side sorting
      if (internalOrderBy === columnId) {
        setInternalOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setInternalOrderBy(columnId);
        setInternalOrder("asc");
      }
    }
  };

  const sortedData: T[] = React.useMemo(() => {
    // If server-side sorting is enabled, don't sort client-side
    if (onSortChange || disableClientSideSort || !orderBy) return data;

    const column = columns.find((c) => c.id === orderBy);
    if (!column || !column.sortable || !column.sortAccessor) return data;

    const accessor = column.sortAccessor;
    const toComparable = (v: unknown): number | string => {
      if (v === null || v === undefined) return "";
      if (v instanceof Date) return v.getTime();
      if (typeof v === "number") return v;
      return String(v).toLowerCase();
    };

    const copy = [...data];
    copy.sort((a, b) => {
      const va = toComparable(accessor(a));
      const vb = toComparable(accessor(b));
      if (va < vb) return order === "asc" ? -1 : 1;
      if (va > vb) return order === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [data, columns, orderBy, order, onSortChange, disableClientSideSort]);

  const defaultEmptyStateContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        py: 6,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: colors.text.secondary,
          fontWeight: 500,
        }}
      >
        No data available
      </Typography>
    </Box>
  );

  const defaultLoadingContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        py: 4,
      }}
    >
      <CircularProgress size={40} sx={{ color: colors.primary.main }} />
      <Typography variant="body2" sx={{ color: colors.text.secondary }}>
        {loadingMessage}
      </Typography>
    </Box>
  );

  return (
    <Paper
      sx={{
        borderRadius: 1,
        border: `1px solid ${colors.border.light}`,
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
        ...paperSx,
      }}
    >
      <TableContainer
        sx={{
          overflow: "auto",
          minHeight,
          // Responsive table container
          "&::-webkit-scrollbar": {
            height: 8,
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: 4,
            "&:hover": {
              backgroundColor: "#a8a8a8",
            },
          },
          // Mobile responsive adjustments
          "@media (max-width: 768px)": {
            "& .MuiTableCell-root": {
              padding: "8px 4px",
              fontSize: "0.75rem",
            },
            "& .MuiTableSortLabel-root": {
              fontSize: "0.75rem",
            },
          },
          ...tableContainerSx,
        }}
      >
        <Table
          stickyHeader={stickyHeader}
          sx={{
            "& td, & th": {
              borderRight: `1px solid ${colors.border.light}`,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& td:last-of-type, & th:last-of-type": {
              borderRight: "none",
            },
            // Responsive table styling
            "& .MuiTableCell-head": {
              backgroundColor: "#f8f9fa",
              fontWeight: 600,
              fontSize: "0.875rem",
              padding: "12px 16px",
              minWidth: "fit-content",
            },
            "& .MuiTableCell-body": {
              fontSize: "0.875rem",
              padding: "12px 16px",
              minWidth: "fit-content",
            },
            // Mobile responsive
            "@media (max-width: 768px)": {
              "& .MuiTableCell-head": {
                padding: "8px 4px",
                fontSize: "0.75rem",
              },
              "& .MuiTableCell-body": {
                padding: "8px 4px",
                fontSize: "0.75rem",
              },
            },
            // High zoom level adjustments
            "@media (min-resolution: 2dppx)": {
              "& .MuiTableCell-head": {
                fontSize: "0.875rem",
                padding: "12px 16px",
              },
              "& .MuiTableCell-body": {
                fontSize: "0.875rem",
                padding: "12px 16px",
              },
            },
            ...tableSx,
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: colors.background.default,
                "& .MuiTableCell-head": {
                  fontWeight: 600,
                  color: colors.text.primary,
                  borderBottom: `2px solid ${colors.border.light}`,
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  sx={{ width: column.width }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                      className="font-medium"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: colors.text.secondary,
                        },
                        "&.Mui-active": {
                          color: `${colors.text.bold}`,
                        },
                        "&:hover": {
                          color: `${colors.text.bold}`,
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <span className="font-medium text-gray-900">
                      {column.label}
                    </span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{
                    textAlign: "center",
                    py: 4,
                    border: "none",
                  }}
                >
                  {defaultLoadingContent}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{
                    textAlign: "center",
                    py: 6,
                    border: "none",
                  }}
                >
                  {emptyStateContent || defaultEmptyStateContent}
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((row, index) => (
                <TableRow
                  onClick={() => onClickRow?.(row)}
                  key={getRowKey ? getRowKey(row, index) : index}
                  sx={{
                    "&:hover": {
                      backgroundColor: colors.background.default,
                    },
                    "& td": {
                      borderBottom: `1px solid ${colors.border.light}`,
                    },
                    ...(getRowSx ? getRowSx(row, index) : {}),
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || "left"}
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 500,
                        ...(column.cellSx || {}),
                      }}
                    >
                      {column.getValue(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={totalCount}
        showFirstButton={showFirstButton}
        showLastButton={showLastButton}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          borderTop: `1px solid ${colors.border.light}`,
          "& .MuiTablePagination-toolbar": {
            paddingLeft: 2,
            paddingRight: 2,
          },
          "& .MuiTablePagination-selectLabel": {
            color: colors.text.secondary,
            fontWeight: 500,
          },
          "& .MuiTablePagination-displayedRows": {
            color: colors.text.secondary,
            fontWeight: 500,
          },
        }}
      />
    </Paper>
  );
}

export default CustomTable;
